import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { Text, Box } from '@atlaskit/primitives/compiled';

import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { PERFORMANCE_INSIGHTS } from '@confluence/named-routes/entry-points/routes.ts';

import { LoadTimesTab } from './load-times/components/LoadTimesTab.tsx';
import { ContentMetricsTab } from './content-metrics/components/ContentMetricsTab.tsx';
import {
	usePerformanceInsightsTab,
	type PerformanceInsightsTab,
} from './hooks/usePerformanceInsightsTab.tsx';

const LEARN_MORE_URL = '#';

const i18n = defineMessages({
	contentMetricsTabTitle: {
		id: 'performance-insights.performance-page.content-metrics-tab-title.non-final',
		defaultMessage: 'Content metrics',
		description:
			'Title for the Content metrics tab on the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	loadTimesTabTitle: {
		id: 'performance-insights.performance-page.load-times-tab-title.non-final',
		defaultMessage: 'Load times',
		description:
			'Title for the Load times tab on the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	pageTitle: {
		id: 'performance-insights.performance-page.title.non-final',
		defaultMessage: 'Performance',
		description:
			'Page title for the Performance analytics page. Page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	pageDescription: {
		id: 'performance-insights.performance-page.description.non-final',
		defaultMessage:
			'Monitor and optimize Confluence with performance tools. Track load times, usage patterns and key performance metrics to spot trends that could impact speed or user experience. Use these insights to identify bottlenecks, stay within recommended limits, and take proactive steps to maintain optimal performance. <a>Review recommendations on improving Confluence performance</a>',
		description:
			'Page description for the Performance analytics page. Also includes a link to documentation about page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
});

interface TabConfig {
	id: PerformanceInsightsTab;
	titleMessage: typeof i18n.loadTimesTabTitle;
	component: React.ComponentType;
}

const TAB_CONFIGS: TabConfig[] = [
	{
		id: 'loadTimes',
		titleMessage: i18n.loadTimesTabTitle,
		component: LoadTimesTab,
	},
	{
		id: 'contentMetrics',
		titleMessage: i18n.contentMetricsTabTitle,
		component: ContentMetricsTab,
	},
];

const styles = cssMap({
	container: {
		margin: token('space.600'),
	},
	descriptionContainer: {
		marginTop: token('space.300'),
	},
});

const PerformanceInsightsPageContent = () => {
	const selectedTab = usePerformanceInsightsTab();
	const { push } = useRouteActions();

	const onTabChange = useCallback(
		(tabIndex: number) => {
			const tabId = TAB_CONFIGS[tabIndex].id;
			// Convert camelCase to kebab-case for URL
			const tabParam = tabId === 'loadTimes' ? 'load-times' : 'content-metrics';

			push(PERFORMANCE_INSIGHTS.toUrl({}, { query: { tab: tabParam } }));
		},
		[push],
	);

	const selectedTabIndex = TAB_CONFIGS.findIndex((tab) => tab.id === selectedTab);

	return (
		<Box xcss={styles.container}>
			<Heading as="h1" size="large">
				<FormattedMessage {...i18n.pageTitle} />
			</Heading>
			<Box xcss={styles.descriptionContainer}>
				<Text>
					<FormattedMessage
						{...i18n.pageDescription}
						values={{
							a: (chunks: React.ReactNode[]) => (
								<Link
									onClick={() => {
										/*Fire analytics event*/
									}}
									href={LEARN_MORE_URL}
									target="blank"
								>
									{chunks}
								</Link>
							),
						}}
					/>
				</Text>
			</Box>
			<Tabs onChange={onTabChange} selected={selectedTabIndex} id="performance-insights-tabs">
				<TabList>
					{TAB_CONFIGS.map((tab) => (
						<Tab key={tab.id}>
							<FormattedMessage {...tab.titleMessage} />
						</Tab>
					))}
				</TabList>
				{TAB_CONFIGS.map((tab) => {
					const TabComponent = tab.component;
					return (
						<TabPanel key={tab.id}>
							<TabComponent />
						</TabPanel>
					);
				})}
			</Tabs>
		</Box>
	);
};

export const PerformanceInsightsPage = () => <PerformanceInsightsPageContent />;
